<div class="el-page">

  <div class="el-header">
    <div class="el-title">
      <i class="material-icons">history</i>
      <div>
        <h5>Extension Usage Logs</h5>
        <span class="el-sub">Reservation history, live session monitoring, utilization and call load reporting</span>
      </div>
    </div>
    <div class="el-actions">
      <button class="el-btn" (click)="exportCsv()" [disabled]="loading">
        <i class="material-icons">download</i> CSV
      </button>
      <button class="el-btn el-btn-accent" (click)="load()" [disabled]="loading">
        <i class="material-icons">refresh</i> Refresh
      </button>
    </div>
  </div>

  <div class="summary-grid">
    <div class="summary-card">
      <span>Total Sessions</span>
      <strong>{{ summary.totalSessions || 0 }}</strong>
    </div>
    <div class="summary-card">
      <span>Average Session</span>
      <strong>{{ formatDuration(summary.averageSessionSeconds || 0) }}</strong>
    </div>
    <div class="summary-card" [class.warn]="summary.longestSession?.longActive">
      <span>Longest Session</span>
      <strong>{{ formatDuration(summary.longestSession?.heldSeconds || 0) }}</strong>
      <small *ngIf="summary.longestSession">
        {{ summary.longestSession.extensionNumber }} - {{ summary.longestSession.userName || 'Unassigned' }}
      </small>
    </div>
    <div class="summary-card">
      <span>Total Calls</span>
      <strong>{{ summary.totalCalls || 0 }}</strong>
    </div>
  </div>

  <div class="el-filters">
    <div class="el-search">
      <i class="material-icons">dialpad</i>
      <input class="el-input" [(ngModel)]="extensionFilter" placeholder="Extension number..." (keyup.enter)="load(true)" />
    </div>
    <select class="el-select" [(ngModel)]="poolFilter">
      <option value="">All pools</option>
      <option *ngFor="let p of poolTypes" [value]="p.code">{{ p.label }}</option>
    </select>
    <div class="el-search user-filter">
      <i class="material-icons">person</i>
      <input class="el-input" [(ngModel)]="userFilter" placeholder="User name..." (keyup.enter)="load(true)" />
    </div>
    <select class="el-select" [(ngModel)]="statusFilter">
      <option value="">Active + released</option>
      <option value="ACTIVE">Active only</option>
      <option value="RELEASED">Released only</option>
    </select>
    <input class="el-date" type="date" [(ngModel)]="fromDate" title="From date" />
    <input class="el-date" type="date" [(ngModel)]="toDate" title="To date" />
    <button class="el-btn el-btn-accent" (click)="load(true)" [disabled]="loading">Search</button>
    <button class="el-btn" (click)="clearFilters()">Clear</button>
    <span class="el-count" *ngIf="!loading">{{ totalItems }} records</span>
  </div>

  <div class="el-error" *ngIf="loadError">{{ loadError }}</div>

  <div class="el-loading" *ngIf="loading">
    <i class="material-icons spin">sync</i> Loading...
  </div>

  <div class="el-table-card" *ngIf="!loading">
    <div class="el-table-wrap">
      <table class="el-table">
        <thead>
          <tr>
            <th>Extension</th>
            <th>Pool</th>
            <th>User</th>
            <th>Reserved At</th>
            <th>Released At</th>
            <th>Duration Held</th>
            <th class="tc">Calls</th>
            <th class="tc">Call Time</th>
            <th class="tc">Utilization</th>
            <th class="tc">Messages</th>
            <th class="tc">Conferences</th>
            <th class="tc">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngIf="!items.length">
            <td colspan="12" class="el-empty">No log records match the filters</td>
          </tr>
          <tr *ngFor="let l of items" [class.row-active]="!l.releasedAt" [class.row-long]="l.longActive">
            <td class="ext-num">{{ l.extensionNumber }}</td>
            <td><span class="pool-pill">{{ l.poolLabel }}</span></td>
            <td class="td-user">{{ l.userName || '-' }}</td>
            <td class="td-sub">{{ l.reservedAt ? (l.reservedAt | date:'dd/MM/yyyy HH:mm') : '-' }}</td>
            <td class="td-sub">
              <span *ngIf="l.releasedAt">{{ l.releasedAt | date:'dd/MM/yyyy HH:mm' }}</span>
              <span *ngIf="!l.releasedAt" class="badge-active">Active</span>
            </td>
            <td class="td-sub">
              {{ formatDuration(l.heldSeconds) }}
              <span *ngIf="l.longActive" class="badge-warn">Over 4h</span>
            </td>
            <td class="tc stat-num">{{ l.callsCount || 0 }}</td>
            <td class="tc stat-num">{{ formatDuration(l.callDurationSeconds || 0) }}</td>
            <td class="tc stat-num">{{ utilization(l) }}</td>
            <td class="tc stat-num">{{ l.messagesCount || 0 }}</td>
            <td class="tc stat-num">{{ l.conferenceCallsCount || 0 }}</td>
            <td class="tc">
              <button class="el-btn danger" *ngIf="!l.releasedAt" (click)="forceRelease(l)">
                <i class="material-icons">logout</i> Release
              </button>
              <span class="td-sub" *ngIf="l.releasedAt">-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="el-pagination" *ngIf="totalPages > 1">
      <button class="el-btn" (click)="goPage(page - 1)" [disabled]="page === 0">
        <i class="material-icons">chevron_left</i>
      </button>
      <span class="el-page-info">Page {{ page + 1 }} of {{ totalPages }}</span>
      <button class="el-btn" (click)="goPage(page + 1)" [disabled]="page >= totalPages - 1">
        <i class="material-icons">chevron_right</i>
      </button>
    </div>
  </div>

  <section class="analysis-card" *ngIf="!loading">
    <div class="analysis-head">
      <div>
        <h5>Call Load Analysis</h5>
        <span>Peak hours, workload distribution and agent utilization signals</span>
      </div>
      <div class="peak-badge">
        <i class="material-icons">bolt</i>
        Peak {{ callLoad.peakHour?.hour || '-' }} · {{ callLoad.peakHour?.calls || 0 }} calls
      </div>
    </div>

    <div class="analysis-grid">
      <div class="chart-panel wide">
        <h6>Hourly Call Volume</h6>
        <div class="hour-bars" *ngIf="callLoad.hourly.length; else noHourly">
          <div class="hour-row" *ngFor="let h of callLoad.hourly">
            <span>{{ h.hour }}</span>
            <div class="bar-track">
              <div class="bar-fill" [style.width]="barWidth(h.calls, maxMetric(callLoad.hourly, 'calls'))"></div>
            </div>
            <strong>{{ h.calls }}</strong>
            <small>{{ formatDuration(h.avgDurationSeconds) }} avg</small>
          </div>
        </div>
        <ng-template #noHourly><div class="empty-mini">No call load data for this range</div></ng-template>
      </div>

      <div class="chart-panel">
        <h6>Calls Per Extension</h6>
        <div class="mini-row" *ngFor="let p of callLoad.callsByExtension">
          <span>{{ p.label }}</span>
          <div><i [style.width]="barWidth(p.value, maxMetric(callLoad.callsByExtension))"></i></div>
          <strong>{{ p.value }}</strong>
        </div>
        <div class="empty-mini" *ngIf="!callLoad.callsByExtension.length">No extension calls</div>
      </div>

      <div class="chart-panel">
        <h6>Calls Per Agent</h6>
        <div class="mini-row" *ngFor="let p of callLoad.callsByAgent">
          <span>{{ p.label }}</span>
          <div><i [style.width]="barWidth(p.value, maxMetric(callLoad.callsByAgent))"></i></div>
          <strong>{{ p.value }}</strong>
        </div>
        <div class="empty-mini" *ngIf="!callLoad.callsByAgent.length">No agent calls</div>
      </div>

      <div class="chart-panel wide">
        <h6>Active Agents vs Calls</h6>
        <div class="dual-row" *ngFor="let h of callLoad.activeAgentsVsCalls">
          <span>{{ h.hour }}</span>
          <div class="dual-bars">
            <i class="agents" [style.width]="barWidth(h.activeAgents, maxMetric(callLoad.activeAgentsVsCalls, 'activeAgents'))"></i>
            <i class="calls" [style.width]="barWidth(h.calls, maxMetric(callLoad.activeAgentsVsCalls, 'calls'))"></i>
          </div>
          <strong>{{ h.activeAgents }} agents / {{ h.calls }} calls</strong>
        </div>
        <div class="empty-mini" *ngIf="!callLoad.activeAgentsVsCalls.length">No active agent chart data</div>
      </div>

      <div class="chart-panel signal">
        <h6>Missed / Abandoned</h6>
        <div class="signal-metric">
          <span>Missed</span>
          <strong>{{ callLoad.missedCalls || 0 }}</strong>
        </div>
        <div class="signal-metric">
          <span>Abandoned</span>
          <strong>{{ callLoad.abandonedCalls || 0 }}</strong>
        </div>
      </div>
    </div>
  </section>

</div>
